import React, { useEffect, useState } from 'react';
import { liveQuery } from 'dexie';
import db from '../db/localdb';
import { dismissFailedOp } from '../sync/repo';

const OP_LABELS = {
  'sale:create': 'Sale', 'sale:void': 'Void sale', 'sale_payment:create': 'Payment',
  'sale_payment:void': 'Void payment', 'purchase:create': 'Restock',
  'stock_adjustment:create': 'Own produce', 'production_run:create': 'Production run',
  'production_run:void': 'Void production run', 'product:create': 'New product',
  'product:update': 'Product change', 'product:void': 'Delete product',
  'customer:create': 'New customer', 'customer:update': 'Customer change',
  'expense:create': 'Expense', 'expense:update': 'Expense change', 'expense:void': 'Delete expense',
};

const describe = (op) => {
  const label = OP_LABELS[`${op.entity}:${op.op_type}`] || `${op.entity} ${op.op_type}`;
  const p = op.payload || {};
  const amount = p.amount ?? (p.qty != null && p.unit_price != null ? `${p.qty} × ${p.unit_price}` : null);
  return [label, p.buyer_name || p.name || p.category, amount].filter(Boolean).join(' · ');
};

export default function SyncStatusBadge() {
  const [online,  setOnline]  = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const [failed,  setFailed]  = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [open,    setOpen]    = useState(false);

  useEffect(() => {
    const on  = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online',  on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  // Live-query the outbox — updates immediately after each repo write/sync.
  useEffect(() => {
    const a = liveQuery(() => db.outbox.filter(op => !op.synced_at && !op.failed_at).count())
      .subscribe({ next: setPending, error: () => setPending(0) });
    const b = liveQuery(() => db.outbox.filter(op => !!op.failed_at && !op.dismissed_at).toArray())
      .subscribe({ next: setFailed, error: () => setFailed([]) });
    return () => { a.unsubscribe(); b.unsubscribe(); };
  }, []);

  // SyncManager fires sb:syncing to show in-progress state.
  useEffect(() => {
    const handler = (e) => setSyncing(e.detail.syncing);
    window.addEventListener('sb:syncing', handler);
    return () => window.removeEventListener('sb:syncing', handler);
  }, []);

  let label, bg, color;
  if (!online) {
    label = pending > 0 ? `○ Offline · ${pending} queued` : '○ Offline';
    bg = '#450a0a'; color = '#fca5a5';
  } else if (syncing) {
    label = '↻ Syncing…';
    bg = '#1e3a5f'; color = '#93c5fd';
  } else if (pending > 0) {
    label = `↑ ${pending} pending`;
    bg = '#78350f'; color = '#fde68a';
  } else {
    label = '● Online';
    bg = '#14532d'; color = '#86efac';
  }

  const pill = { fontSize: '0.72rem', fontWeight: 600, padding: '0.2rem 0.6rem', borderRadius: '999px', border: 'none' };

  return (
    <span style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center', position: 'relative' }}>
      <span style={{ ...pill, background: bg, color }}>{label}</span>
      {failed.length > 0 && (
        <button style={{ ...pill, background: '#7f1d1d', color: '#fecaca', cursor: 'pointer' }}
          onClick={() => setOpen(o => !o)} aria-expanded={open}>
          ⚠ {failed.length} not saved
        </button>
      )}
      {open && failed.length > 0 && (
        <div role="dialog" className="card" style={{
          position: 'absolute', top: '1.8rem', right: 0, zIndex: 50, width: 'min(22rem, 90vw)',
          boxShadow: '0 10px 30px rgba(0,0,0,0.4)', fontSize: '0.8rem',
        }}>
          <div style={{ fontWeight: 700, marginBottom: '0.35rem' }}>Not saved on the server</div>
          <p className="text-muted" style={{ fontSize: '0.75rem', marginBottom: '0.6rem' }}>
            These were recorded on this phone but the server refused them, so they are not in your records.
            Re-enter them correctly, then dismiss.
          </p>
          {failed.map(op => (
            <div key={op._localId} style={{ borderTop: '1px solid var(--color-border)', padding: '0.45rem 0' }}>
              <div style={{ fontWeight: 600 }}>{describe(op)}</div>
              <div style={{ color: 'var(--color-danger)' }}>{op.error}</div>
              <button className="btn-ghost" style={{ marginTop: '0.3rem', padding: '0.15rem 0.5rem', fontSize: '0.72rem' }}
                onClick={() => dismissFailedOp(op._localId)}>
                Dismiss
              </button>
            </div>
          ))}
        </div>
      )}
    </span>
  );
}
